import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Modals can stack (the cancel confirmation opens over "Gestionar"). The stack holds the dialog refs in opening
// order: only the top-most one answers Escape and traps Tab. The body scroll is locked by the first one and
// restored when the last one closes, whatever order React unmounts them in.
const modalStack = [];
let savedOverflow = "";

const pushModal = (entry) => {
  if (modalStack.length === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  modalStack.push(entry);
};

const removeModal = (entry) => {
  const index = modalStack.indexOf(entry);
  if (index >= 0) modalStack.splice(index, 1);
  if (modalStack.length === 0) document.body.style.overflow = savedOverflow;
};

const isTopModal = (entry) => modalStack[modalStack.length - 1] === entry;

const focusablesOf = (dialog) => Array.from(dialog.querySelectorAll(FOCUSABLE));

const canFocus = (element) => Boolean(element) && element.isConnected && typeof element.focus === "function";

// After a modal closes: its return target or opener when still on the page, unless another modal is still open
// and the target is outside it; then that modal takes the focus (its first control, or the dialog itself).
const restoreFocus = (returnTarget, opener) => {
  const target = [returnTarget, opener].find((element) => canFocus(element) && element !== document.body);
  const below = modalStack[modalStack.length - 1]?.current;
  if (below && !(target && below.contains(target))) {
    (focusablesOf(below)[0] || below).focus();
    return;
  }
  if (target) target.focus();
};

/**
 * Modal of the Rollo section (design Modal): scrim, header with the step label, title and progress bar, a body
 * that scrolls on its own and a fixed footer. It renders in document.body (with the section tokens) so no
 * ancestor can clip it. Closes with Escape, a click on the scrim, the cross or the footer button, unless
 * closeDisabled (while saving, or while another modal is open on top). With stacked modals only the top-most one
 * handles Escape and the Tab focus trap. On close the focus goes back to returnFocusRef (or the opener), or to the
 * modal underneath when one is still open. headExtra goes under the title (data of the gestión), bodyClassName
 * replaces the body layout, className adds the section scope to the overlay (".crm-ipc", so its styles reach the
 * portal) and role="alertdialog" marks a confirmation.
 */
export default function Modal({
  titleId,
  title,
  stepLabel,
  steps = 0,
  step = 0,
  size = "md",
  role = "dialog",
  darkMode = false,
  closeDisabled = false,
  returnFocusRef,
  onClose,
  headExtra,
  bodyClassName,
  className,
  footer,
  children,
}) {
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  const closeDisabledRef = useRef(closeDisabled);
  closeRef.current = onClose;
  closeDisabledRef.current = closeDisabled;

  useEffect(() => {
    const opener = document.activeElement;
    const returnTarget = returnFocusRef?.current;
    // The ref object itself is the stack entry: one per mounted modal.
    const entry = dialogRef;
    pushModal(entry);

    const handleKeyDown = (event) => {
      if (!isTopModal(entry)) return;
      if (event.key === "Escape") {
        if (!closeDisabledRef.current) closeRef.current?.();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusables = focusablesOf(dialogRef.current);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!dialogRef.current.contains(document.activeElement)) {
        // Focus was left outside the top-most modal (for example in the modal underneath): bring it back.
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      removeModal(entry);
      restoreFocus(returnTarget, opener);
    };
  }, [returnFocusRef]);

  const handleScrim = (event) => {
    if (event.target === event.currentTarget && !closeDisabled) onClose?.();
  };

  return createPortal(
    <div
      className={`crm-rollo crm-rollo-overlay${className ? ` ${className}` : ""}${darkMode ? " is-dark" : ""}`}
      onMouseDown={handleScrim}
    >
      <div
        ref={dialogRef}
        className={`crm-rollo__modal crm-rollo__modal--${size}`}
        role={role}
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="crm-rollo__modal-head">
          <div className="crm-rollo__modal-head-main">
            {stepLabel ? <div className="crm-rollo__modal-step">{stepLabel}</div> : null}
            <h2 id={titleId} className="crm-rollo__modal-title">
              {title}
            </h2>
            {headExtra}
            {steps > 0 ? (
              <div className="crm-rollo__steps" aria-hidden="true">
                {Array.from({ length: steps }, (_, index) => (
                  <i key={index} className={index < step ? "is-on" : undefined} />
                ))}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="crm-rollo__btn crm-rollo__btn--ghost crm-rollo__btn--sm crm-rollo__btn--icon"
            aria-label="Cerrar"
            disabled={closeDisabled}
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
          </button>
        </div>
        <div className={`crm-rollo__modal-body${bodyClassName ? ` ${bodyClassName}` : ""}`}>{children}</div>
        {footer ? <div className="crm-rollo__modal-foot">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
}
